import { Box, Typography, useTheme } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { FlatRowTriggerButton } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerButton";
import { FlatRowTriggerValue } from "web_myplann/Shared/Components/FlatRow/FlatRowTriggerValue";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type FlatRowSelectOption<TValue extends string | number = string> = {
	value: TValue;
	label: string;
};

type FlatRowSelectCommonProps<TValue extends string | number = string> = {
	label: string;
	options: FlatRowSelectOption<TValue>[];
	onBlur?: () => void;
	drawerTitle?: string;
	isLast?: boolean;
	disabled?: boolean;
	error?: boolean;
	helperText?: string;
	placeholder?: string;
};

export type FlatRowSelectProps<TValue extends string | number = string> =
	FlatRowSelectCommonProps<TValue> &
		(
			| {
					multiple?: false;
					value: TValue | "";
					onChange: (value: TValue) => void;
			  }
			| {
					multiple: true;
					value: TValue[];
					onChange: (value: TValue[]) => void;
			  }
		);

export const FlatRowSelect = <TValue extends string | number = string>({
	label,
	options,
	onBlur,
	drawerTitle,
	isLast,
	disabled,
	error,
	helperText,
	placeholder,
	...selection
}: FlatRowSelectProps<TValue>) => {
	const theme = useTheme();
	const hasGoalsRight = useGoalsRightOptional();
	const [open, setOpen] = useState(false);

	const selectedValues = selection.multiple
		? selection.value
		: selection.value === ""
			? []
			: [selection.value];

	const selectedLabel = options
		.filter((option) =>
			selectedValues.some(
				(selected) => String(selected) === String(option.value),
			),
		)
		.map((option) => option.label)
		.join(", ");

	const handleSelect = (optionValue: TValue) => {
		if (selection.multiple) {
			const isSelected = selection.value.some(
				(selected) => String(selected) === String(optionValue),
			);
			selection.onChange(
				isSelected
					? selection.value.filter(
							(selected) => String(selected) !== String(optionValue),
						)
					: [...selection.value, optionValue],
			);
			return;
		}

		selection.onChange(optionValue);
		setOpen(false);
	};

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	return (
		<Box>
			<FlatRowTriggerButton
				onClick={() => setOpen(true)}
				disabled={disabled}
				sx={{
					borderBottom: isLast
						? "none"
						: `1px solid ${theme.palette.surface.border}`,
				}}
			>
				<Typography
					variant="body2"
					color={error ? theme.palette.error.main : theme.palette.grey[500]}
					flexShrink={0}
					mr={2}
					lineHeight={`${VALUE_LINE_HEIGHT}px`}
				>
					{label}
				</Typography>
				<FlatRowTriggerValue
					value={selectedLabel}
					placeholder={placeholder}
					icon={hasGoalsRight ? ChevronDown : undefined}
					disabled={disabled}
				/>
			</FlatRowTriggerButton>

			{error && helperText && (
				<Typography
					component="p"
					variant="caption"
					color={theme.palette.error.main}
					px={1.6}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
				grabber
			>
				<FlatRowSelectOptionList
					options={options}
					value={selectedValues}
					onSelect={handleSelect}
				/>
			</MyPlannDrawer>
		</Box>
	);
};
